import * as Popover from "@radix-ui/react-popover";
import { Slot } from "@radix-ui/react-slot";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export function TermExplanation({ explanation, children, asChild = false, contentClassName, side = "top", align = "center" }: Readonly<{
  explanation: ReactNode;
  children: ReactNode;
  asChild?: boolean;
  contentClassName?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
}>) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();
  const Anchor = asChild ? Slot : "span";
  const cancelClose = () => { if (timer.current) clearTimeout(timer.current); };
  const show = () => { cancelClose(); setOpen(true); };
  const close = () => { cancelClose(); setOpen(false); };
  const scheduleClose = () => { cancelClose(); timer.current = setTimeout(() => setOpen(false), 150); };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return <Popover.Root open={open} onOpenChange={setOpen}>
    <Popover.Anchor asChild><Anchor ref={anchor} className="term-explanation" tabIndex={0}
      aria-describedby={open ? id : undefined} onFocus={show} onBlur={close}
      onPointerEnter={event => { if (event.pointerType !== "touch") show(); }}
      onPointerLeave={event => { if (event.pointerType !== "touch") scheduleClose(); }}
      onKeyDown={event => { if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); close(); } }}>
      {children}
    </Anchor></Popover.Anchor>
    <Popover.Portal><Popover.Content id={id} role="tooltip" className={`term-explanation-content${contentClassName ? ` ${contentClassName}` : ""}`}
      side={side} align={align} sideOffset={8} collisionPadding={12}
      onOpenAutoFocus={event => event.preventDefault()} onCloseAutoFocus={event => event.preventDefault()}
      onPointerEnter={cancelClose} onPointerLeave={scheduleClose}
      onInteractOutside={event => { if (anchor.current?.contains(event.target as Node)) event.preventDefault(); }}
      onEscapeKeyDown={close}>
      {explanation}
    </Popover.Content></Popover.Portal>
  </Popover.Root>;
}
